import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import NewICConversationModal from './NewICConversationModal';
import {
    MessageCircle,
    Send,
    Plus,
    ChevronLeft,
    Paperclip,
    X,
    FileText
} from 'lucide-react';
import './EventMessages.css';
import './EventHome.css';

// Attachment validation constants
const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['application/pdf'];

// Helper to format file size
const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper to extract filename from URL
const getFilenameFromUrl = (url, fallbackTitle) => {
    try {
        // Extract path from URL (before query params)
        const urlPath = url.split('?')[0];
        // Get the last part of the path (filename)
        const parts = urlPath.split('/');
        const filename = parts[parts.length - 1];
        // Decode URI
        const decoded = decodeURIComponent(filename);

        let cleanedName = decoded;

        // Pattern 1: Remove numeric prefix like "0-1766176455-" or "2-1766176455-"
        cleanedName = cleanedName.replace(/^\d+-\d+-/, '');

        // Pattern 2: Remove hash prefix like "c1f104deb001156de88b00cf39c85dc3."
        // Hash is typically 32 hex characters followed by a dot
        cleanedName = cleanedName.replace(/^[a-f0-9]{32}\./, '');

        // Ensure .pdf extension if it's a PDF but missing extension
        if (cleanedName && !cleanedName.toLowerCase().endsWith('.pdf')) {
            cleanedName = cleanedName + '.pdf';
        }

        return cleanedName || fallbackTitle || 'Attachment';
    } catch {
        return fallbackTitle || 'Attachment';
    }
};

const EventMessages = () => {
    const { eventNameId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    useNavigate(); // Keep for potential future use
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Chat state (iConnections)
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatDetails, setChatDetails] = useState(null);
    const [chatsLoading, setChatsLoading] = useState(false);

    // Messages state
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // Attachment state
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');

    // New conversation modal
    const [showNewConversation, setShowNewConversation] = useState(false);

    // Mobile state
    const [showChatList, setShowChatList] = useState(true);

    // Refs
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const fileInputRef = useRef(null);

    // Get chat_id from URL query params
    const chatIdFromUrl = searchParams.get('chat_id');

    // Fetch event data
    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch chats when event is loaded
    useEffect(() => {
        if (event?.EventId) {
            fetchChats();
            startPolling();
        }
        return () => stopPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.EventId]);

    // Handle chat_id from URL
    useEffect(() => {
        if (chatIdFromUrl && chats.length > 0) {
            // If selectedChat already matches the URL, don't override it
            // This prevents race conditions when creating new chats
            if (selectedChat?.id === chatIdFromUrl) {
                return;
            }

            const chatFromUrl = chats.find(c => c.id === chatIdFromUrl);
            if (chatFromUrl) {
                setSelectedChat(chatFromUrl);
                setShowChatList(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatIdFromUrl, chats]);

    // Fetch chat details when chat is selected
    useEffect(() => {
        if (selectedChat) {
            fetchChatDetails(selectedChat.id);
            markChatAsRead(selectedChat.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChat?.id]);

    // Scroll to bottom when chat details change
    useEffect(() => {
        // Use setTimeout to ensure DOM has rendered
        const timer = setTimeout(() => {
            scrollToBottom(false); // instant scroll on load
        }, 100);
        return () => clearTimeout(timer);
    }, [chatDetails]);

    const fetchEventData = async () => {
        try {
            setLoading(true);
            const response = await apiService.getEventByNameId(eventNameId);
            setEvent(response.data);
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
        } finally {
            setLoading(false);
        }
    };

    const fetchChats = async (preserveOptimistic = true) => {
        try {
            setChatsLoading(true);
            // Pass eventId to filter chats by confirmed F4C attendees
            const response = await apiService.getICChats(event?.EventId);
            if (response.data.success) {
                const serverChats = response.data.chats || [];

                if (preserveOptimistic) {
                    // Merge: keep optimistically added chats that aren't in the server response yet
                    setChats(prev => {
                        const serverChatIds = new Set(serverChats.map(c => c.id));
                        // Keep any chats from prev that aren't in server response (optimistic adds)
                        const optimisticChats = prev.filter(c => !serverChatIds.has(c.id));
                        // Put optimistic chats first, then server chats
                        return [...optimisticChats, ...serverChats];
                    });
                } else {
                    setChats(serverChats);
                }
            } else {
                console.error('Failed to fetch chats:', response.data.error);
            }
        } catch (err) {
            console.error('Error fetching chats:', err);
        } finally {
            setChatsLoading(false);
        }
    };

    const fetchChatDetails = async (chatId) => {
        try {
            setMessagesLoading(true);
            const response = await apiService.getICChatMessages(chatId);
            if (response.data.success) {
                setChatDetails(response.data.data);
            } else {
                console.error('Failed to fetch chat details:', response.data.error);
            }
        } catch (err) {
            console.error('Error fetching chat details:', err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const markChatAsRead = async (chatId) => {
        try {
            await apiService.markICChatRead(chatId);
            // Update local chat state
            setChats(prev => prev.map(c =>
                c.id === chatId ? { ...c, unreadCount: 0 } : c
            ));
            // Trigger refresh of unread count in navigation
            window.dispatchEvent(new Event('refreshUnreadCount'));
        } catch (err) {
            console.error('Error marking chat as read:', err);
        }
    };

    // File attachment handlers
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        setAttachmentError('');

        for (const file of files) {
            // Check max attachments
            if (attachments.length >= MAX_ATTACHMENTS) {
                setAttachmentError(`Maximum ${MAX_ATTACHMENTS} attachments allowed per message.`);
                break;
            }

            // Check file type
            if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                setAttachmentError(`Only PDF files are allowed. "${file.name}" is not a valid file type.`);
                continue;
            }

            // Check file size
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setAttachmentError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
                continue;
            }

            // Convert to base64
            try {
                const base64 = await fileToBase64(file);
                setAttachments(prev => [...prev, {
                    base64: base64.split(',')[1], // Remove data:application/pdf;base64, prefix
                    fileName: file.name,
                    fileType: file.type
                }]);
            } catch (err) {
                console.error('Error converting file to base64:', err);
                setAttachmentError(`Error processing "${file.name}".`);
            }
        }

        // Clear the input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
        setAttachmentError('');
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedChat || sendingMessage) return;

        try {
            setSendingMessage(true);
            const response = await apiService.sendICMessage(selectedChat.id, newMessage.trim(), attachments);

            if (response.data.success) {
                setNewMessage('');
                setAttachments([]);
                setAttachmentError('');
                // Refresh chat details to get the new message
                fetchChatDetails(selectedChat.id);

                // Update chat's last message in the list
                setChats(prev => prev.map(c =>
                    c.id === selectedChat.id
                        ? { ...c, lastMessage: newMessage.trim() || `Sent ${attachments.length} attachment(s)`, lastMessageDate: new Date().toLocaleDateString() }
                        : c
                ));

                // Focus back on input
                messageInputRef.current?.focus();
            } else {
                setAttachmentError(response.data.error || 'Failed to send message.');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setAttachmentError(err.response?.data?.detail || 'Error sending message.');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleChatSelect = (chat) => {
        setSelectedChat(chat);
        setShowChatList(false);
        // Update URL with chat_id
        setSearchParams({ chat_id: chat.id });
    };

    const handleBackToChats = () => {
        setShowChatList(true);
        setSelectedChat(null);
        setChatDetails(null);
        // Remove chat_id from URL
        setSearchParams({});
    };

    const handleStartConversation = async (selectedContacts) => {
        try {
            // Extract the ic_contact_guid values from selected contacts
            const userGuids = selectedContacts
                .map(contact => contact.ic_contact_guid)
                .filter(guid => guid); // Filter out any null/undefined

            if (userGuids.length === 0) {
                console.error('No valid contact GUIDs found');
                return;
            }

            // Call the API to create the chat
            const response = await apiService.createICChat(userGuids);

            if (response.data.success && response.data.chat) {
                // Get the chat ID from the response
                const chatData = response.data.chat;
                const chatId = chatData.chatId || chatData.id;

                if (chatId) {
                    // Close the modal first
                    setShowNewConversation(false);
                    setShowChatList(false);

                    // Build user objects from selected contacts for immediate display
                    const chatUsers = selectedContacts.map(contact => {
                        const firstName = contact.first_name || '';
                        const lastName = contact.last_name || '';
                        const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';

                        return {
                            contactId: contact.contact_id,
                            contactGuid: contact.ic_contact_guid,
                            firstName: firstName,
                            lastName: lastName,
                            fullName: `${firstName} ${lastName}`.trim() || 'Unknown',
                            imageUrl: contact.image_url || null,
                            companyName: contact.company_name || '',
                            initials: initials
                        };
                    });

                    // Build display name from contacts
                    const displayName = chatUsers.map(u => u.fullName).join(', ');

                    // Create immediate chat object for sidebar
                    // Always use chatUsers we built (not chatData.users) since we have the correct structure
                    const immediateChat = {
                        id: chatId,
                        name: chatData.name || chatData.groupName || displayName,
                        users: chatUsers, // Use our well-structured users, not API response
                        lastMessage: 'No messages yet',
                        lastMessageDate: new Date().toLocaleDateString(),
                        unreadCount: 0,
                        chatType: selectedContacts.length > 1 ? 'group' : 'direct',
                        _isOptimistic: true // Flag to identify optimistic entries
                    };

                    // Immediately add to sidebar and select
                    setChats(prev => [immediateChat, ...prev.filter(c => c.id !== chatId)]);
                    setSelectedChat(immediateChat);

                    // Update URL to the new chat
                    setSearchParams({ chat_id: chatId });

                    // Fetch the chat details/messages
                    fetchChatDetails(chatId);

                    // Poll for the chat to appear in the server list, then update with real data
                    const pollForChat = async (attempts = 0) => {
                        if (attempts >= 10) return; // Give up after 10 attempts (10 seconds)

                        try {
                            const chatsResponse = await apiService.getICChats(event?.EventId);
                            if (chatsResponse.data.success) {
                                const serverChats = chatsResponse.data.chats || [];
                                const serverChat = serverChats.find(c => c.id === chatId);

                                if (serverChat) {
                                    // Found it! Update with real server data
                                    setChats(serverChats);
                                    // Update selected chat if still viewing this one
                                    setSelectedChat(prev => prev?.id === chatId ? serverChat : prev);
                                } else {
                                    // Not found yet, keep optimistic chat and try again
                                    setChats(prev => {
                                        const hasOptimistic = prev.some(c => c.id === chatId);
                                        if (hasOptimistic) {
                                            // Merge: keep optimistic, add any new server chats
                                            const optimistic = prev.find(c => c.id === chatId);
                                            const otherServerChats = serverChats.filter(c => c.id !== chatId);
                                            return [optimistic, ...otherServerChats];
                                        }
                                        return serverChats;
                                    });
                                    // Retry after 1 second
                                    setTimeout(() => pollForChat(attempts + 1), 1000);
                                }
                            }
                        } catch (err) {
                            console.error('Error polling for new chat:', err);
                            setTimeout(() => pollForChat(attempts + 1), 1000);
                        }
                    };

                    // Start polling after a short delay
                    setTimeout(() => pollForChat(0), 500);
                }
            } else {
                console.error('Failed to create chat:', response.data.error);
            }
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
    };

    // Polling for new messages
    const startPolling = useCallback(() => {
        if (pollIntervalRef.current) return;

        pollIntervalRef.current = setInterval(async () => {
            // Refresh chats periodically
            try {
                // Pass eventId to filter chats by confirmed F4C attendees
                const response = await apiService.getICChats(event?.EventId);
                if (response.data.success) {
                    const serverChats = response.data.chats || [];
                    // Merge: keep optimistically added chats that aren't in server response yet
                    setChats(prev => {
                        const serverChatIds = new Set(serverChats.map(c => c.id));
                        const optimisticChats = prev.filter(c => !serverChatIds.has(c.id));
                        return [...optimisticChats, ...serverChats];
                    });
                }

                // If a chat is selected, refresh chat details too
                if (selectedChat) {
                    const msgResponse = await apiService.getICChatMessages(selectedChat.id);
                    if (msgResponse.data.success) {
                        setChatDetails(msgResponse.data.data);
                    }
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 30000); // Poll every 30 seconds
    }, [selectedChat, event?.EventId]);

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const scrollToBottom = (smooth = true) => {
        messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    };

    // Helper to filter out current user from chat users
    const getOtherUsers = (users) => {
        return users?.filter(u => {
            const matchesById = u.contactId === user?.contact_id;
            const matchesByGuid = user?.ic_contact_guid &&
                u.contactGuid?.toUpperCase() === user.ic_contact_guid.toUpperCase();
            return !matchesById && !matchesByGuid;
        }) || [];
    };

    // Helper functions for iConnections chat data
    const getChatDisplayName = (chat) => {
        // If chat has a name (from group name or iConnections), use it
        if (chat.name) {
            return chat.name;
        }

        // Otherwise, build name from other users
        const otherUsers = getOtherUsers(chat.users);
        if (otherUsers.length > 0) {
            const names = otherUsers.map(u => u.fullName || `${u.firstName} ${u.lastName}`);
            return names.join(' and ');
        }

        return 'Unknown';
    };

    const getChatAvatar = (chat) => {
        // Get other users (exclude current user)
        const otherUsers = getOtherUsers(chat.users);

        // For single user - just show large avatar
        if (otherUsers.length === 1) {
            const otherUser = otherUsers[0];
            if (otherUser?.imageUrl) {
                return <img src={otherUser.imageUrl} alt="" />;
            }
            return otherUser?.initials || chat.name?.[0] || '?';
        }

        // For 2 users - show 2 stacked avatars
        if (otherUsers.length === 2) {
            return (
                <div className="stacked-avatars">
                    {otherUsers.map((u, idx) => (
                        <div key={u.contactId || idx} className={`stacked-avatar stacked-avatar-${idx}`}>
                            {u.imageUrl ? (
                                <img src={u.imageUrl} alt="" />
                            ) : (
                                <span>{u.initials || u.firstName?.[0] || '?'}</span>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // For 3+ users - show grid of avatars with minimal overlap
        if (otherUsers.length >= 3) {
            // Show up to 4 avatars, rest as +X
            const maxVisible = 4;
            const visibleUsers = otherUsers.slice(0, maxVisible);
            const extraCount = otherUsers.length - maxVisible;
            const gridClass = visibleUsers.length === 3 ? 'grid-3' : 'grid-4';

            return (
                <div className={`avatar-grid ${gridClass}`}>
                    {visibleUsers.map((u, idx) => (
                        <div key={u.contactId || idx} className="avatar-grid-item">
                            {u.imageUrl ? (
                                <img src={u.imageUrl} alt="" />
                            ) : (
                                <span>{u.initials || u.firstName?.[0] || '?'}</span>
                            )}
                        </div>
                    ))}
                    {extraCount > 0 && (
                        <div className="avatar-grid-extra">
                            <span>+{extraCount}</span>
                        </div>
                    )}
                </div>
            );
        }

        // Fallback to chat image or initials
        if (chat.imageUrl) {
            return <img src={chat.imageUrl} alt="" />;
        }

        return chat.name?.[0]?.toUpperCase() || '?';
    };

    const getChatSubtitle = (chat) => {
        const otherUsers = getOtherUsers(chat.users);
        if (otherUsers.length === 1) {
            return otherUsers[0]?.companyName || '';
        }
        // For groups, show participant count
        if (chat.users?.length > 0) {
            return `${chat.users.length} participants`;
        }
        return '';
    };

    const formatMessageTime = (dateStr) => {
        if (!dateStr) return '';

        // Handle MM/DD/YY format from iConnections
        if (dateStr.includes('/')) {
            return dateStr;
        }

        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;

        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
        if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    // Get header info from chat details or selected chat
    const getHeaderInfo = () => {
        if (chatDetails) {
            // Get other users (exclude current user)
            const otherUsers = getOtherUsers(chatDetails.users);

            // For named group chats
            if (chatDetails.groupName) {
                return {
                    name: chatDetails.groupName,
                    subtitle: `${chatDetails.users?.length || 0} participants`,
                    isGroup: true,
                    imageUrl: chatDetails.groupImageUrl,
                    users: otherUsers
                };
            }

            // For single chats (one other user)
            if (otherUsers.length === 1) {
                const otherUser = otherUsers[0];
                return {
                    name: otherUser.name,
                    subtitle: otherUser.companyName,
                    isGroup: false,
                    imageUrl: otherUser.imageUrl,
                    initials: otherUser.initials,
                    users: otherUsers
                };
            }

            // For multi-user chats without a group name (show "Name and Name")
            if (otherUsers.length > 1) {
                const names = otherUsers.map(u => u.name || `${u.firstName} ${u.lastName}`);
                const displayName = names.join(' and ');
                return {
                    name: displayName,
                    subtitle: `${chatDetails.users?.length || 0} participants`,
                    isGroup: true,
                    imageUrl: null,
                    users: otherUsers
                };
            }
        }
        // Fallback to selected chat info
        return {
            name: getChatDisplayName(selectedChat),
            subtitle: getChatSubtitle(selectedChat),
            isGroup: selectedChat?.chatType === 'group' || (selectedChat?.users?.length > 2),
            imageUrl: null,
            users: getOtherUsers(selectedChat?.users)
        };
    };

    // Render loading state
    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading messages..." />
                    </div>
                </div>
            </>
        );
    }

    // Render error state
    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const totalUnread = chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    const headerInfo = selectedChat ? getHeaderInfo() : null;

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="messages-container">
                        {/* Chat List Sidebar */}
                        <div className={`threads-sidebar ${!showChatList ? 'hidden' : ''}`}>
                            <div className="threads-header">
                                <h2>Messages {totalUnread > 0 && <span className="unread-badge">{totalUnread}</span>}</h2>
                                <button
                                    className="new-conversation-btn"
                                    onClick={() => setShowNewConversation(true)}
                                >
                                    <Plus size={18} />
                                    New
                                </button>
                            </div>

                            <div className="threads-list">
                                {chatsLoading ? (
                                    <div className="messages-loading">
                                        <div className="spinner"></div>
                                    </div>
                                ) : chats.length === 0 ? (
                                    <div className="empty-threads">
                                        <MessageCircle size={60} />
                                        <p>No conversations yet.<br />Start a new conversation!</p>
                                    </div>
                                ) : (
                                    chats.map(chat => (
                                        <div
                                            key={chat.id}
                                            className={`thread-item ${selectedChat?.id === chat.id ? 'active' : ''} ${chat.unreadCount > 0 ? 'unread' : ''}`}
                                            onClick={() => handleChatSelect(chat)}
                                        >
                                            <div className={`thread-avatar ${chat.chatType === 'group' ? 'group' : ''}`}>
                                                {getChatAvatar(chat)}
                                            </div>
                                            <div className="thread-info">
                                                <div className="thread-top-row">
                                                    <span className="thread-name">{getChatDisplayName(chat)}</span>
                                                    <span className="thread-time">
                                                        {formatMessageTime(chat.lastMessageDate)}
                                                    </span>
                                                </div>
                                                <div className="thread-bottom-row">
                                                    <span className={`thread-preview ${chat.unreadCount > 0 ? 'unread' : ''}`}>
                                                        {chat.lastMessage || 'No messages yet'}
                                                    </span>
                                                    {chat.unreadCount > 0 && (
                                                        <span className="unread-badge">{chat.unreadCount}</span>
                                                    )}
                                                </div>
                                                {getChatSubtitle(chat) && (
                                                    <div className="thread-company">
                                                        {getChatSubtitle(chat)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Message Area */}
                        <div className={`message-area ${showChatList ? 'hidden' : ''}`}>
                            {selectedChat ? (
                                <>
                                    {/* Message Header */}
                                    <div className="message-header">
                                        <div className="message-header-info">
                                            <button className="mobile-back-btn" onClick={handleBackToChats}>
                                                <ChevronLeft size={24} />
                                            </button>
                                            <div className={`message-header-avatar ${headerInfo?.users?.length > 1 ? 'no-bg' : ''}`}>
                                                {headerInfo?.users?.length === 2 ? (
                                                    // 2 users - stacked avatars
                                                    <div className="stacked-avatars header-stacked">
                                                        {headerInfo.users.map((u, idx) => (
                                                            <div key={u.contactId || idx} className={`stacked-avatar stacked-avatar-${idx}`}>
                                                                {u.imageUrl ? (
                                                                    <img src={u.imageUrl} alt="" />
                                                                ) : (
                                                                    <span>{u.initials || u.name?.[0] || '?'}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : headerInfo?.users?.length >= 3 ? (
                                                    // 3+ users - stacked avatars with extra indicator
                                                    <div className="stacked-avatars header-stacked multi">
                                                        {headerInfo.users.slice(0, 2).map((u, idx) => (
                                                            <div key={u.contactId || idx} className={`stacked-avatar stacked-avatar-${idx}`}>
                                                                {u.imageUrl ? (
                                                                    <img src={u.imageUrl} alt="" />
                                                                ) : (
                                                                    <span>{u.initials || u.name?.[0] || '?'}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <div className="stacked-avatar stacked-avatar-extra">
                                                            <span>+{headerInfo.users.length - 2}</span>
                                                        </div>
                                                    </div>
                                                ) : headerInfo?.imageUrl ? (
                                                    <img src={headerInfo.imageUrl} alt="" />
                                                ) : (
                                                    headerInfo?.initials || headerInfo?.name?.[0] || '?'
                                                )}
                                            </div>
                                            <div className="message-header-details">
                                                <h3>{headerInfo?.name}</h3>
                                                <div className="message-header-participants">
                                                    {headerInfo?.subtitle}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Messages Display */}
                                    <div className="messages-display">
                                        {messagesLoading ? (
                                            <div className="messages-loading">
                                                <div className="spinner"></div>
                                            </div>
                                        ) : !chatDetails?.messageBlocks || chatDetails.messageBlocks.length === 0 ? (
                                            <div className="no-messages">
                                                <p>No messages yet. Start the conversation!</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Reverse blocks to show oldest dates first (e.g., Yesterday before Today) */}
                                                {[...chatDetails.messageBlocks].reverse().map((block, blockIndex) => {
                                                    // Sort messages chronologically within each block (oldest first, newest at bottom)
                                                    const sortedMessages = [...block.messages].sort((a, b) => {
                                                        // Parse timestamps like "9:17 PM" or "2:14 PM"
                                                        const parseTime = (timeStr) => {
                                                            if (!timeStr) return 0;
                                                            const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
                                                            if (!match) return 0;
                                                            let hours = parseInt(match[1]);
                                                            const minutes = parseInt(match[2]);
                                                            const period = match[3].toUpperCase();
                                                            if (period === 'PM' && hours !== 12) hours += 12;
                                                            if (period === 'AM' && hours === 12) hours = 0;
                                                            return hours * 60 + minutes;
                                                        };
                                                        return parseTime(a.timeStamp) - parseTime(b.timeStamp);
                                                    });

                                                    return (
                                                        <React.Fragment key={blockIndex}>
                                                            {/* Date separator */}
                                                            <div className="date-separator">
                                                                <span>{block.header}</span>
                                                            </div>

                                                            {/* Messages in this block - sorted chronologically (oldest first, newest at bottom) */}
                                                            {sortedMessages.map((message) => {
                                                                // Check by ic_contact_guid first, fallback to contactId
                                                                const isOwnMessage = user?.ic_contact_guid
                                                                    ? message.sender?.contactGuid?.toUpperCase() === user.ic_contact_guid.toUpperCase()
                                                                    : message.sender?.contactId === user?.contact_id;
                                                                const isSystem = message.isSystem;

                                                                if (isSystem) {
                                                                    return (
                                                                        <div key={message.messageId} className="message-bubble system-message">
                                                                            <div className="message-content">
                                                                                {message.body}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <div
                                                                        key={message.messageId}
                                                                        className={`message-bubble ${isOwnMessage ? 'sent' : 'received'}`}
                                                                    >
                                                                        {!isOwnMessage && (
                                                                            <div className="message-sender-info">
                                                                                <div className="message-sender-avatar">
                                                                                    {message.sender?.imageUrl ? (
                                                                                        <img src={message.sender.imageUrl} alt="" />
                                                                                    ) : (
                                                                                        message.sender?.initials || message.sender?.name?.[0] || '?'
                                                                                    )}
                                                                                </div>
                                                                                <span className="message-sender-name">
                                                                                    {message.sender?.name || 'Unknown'}
                                                                                </span>
                                                                                {message.sender?.companyName && (
                                                                                    <span className="message-sender-company">
                                                                                        {message.sender.companyName}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        <div className="message-content">
                                                                            {message.body}
                                                                        </div>
                                                                        {/* Message attachments */}
                                                                        {message.attachments && message.attachments.length > 0 && (
                                                                            <div className="message-attachments">
                                                                                {message.attachments.map((att, attIndex) => (
                                                                                    <a
                                                                                        key={attIndex}
                                                                                        href={att.url}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="message-attachment-link"
                                                                                    >
                                                                                        <FileText size={16} />
                                                                                        <span className="attachment-title">
                                                                                            {getFilenameFromUrl(att.url, att.title)}
                                                                                        </span>
                                                                                        {att.fileSize && (
                                                                                            <span className="attachment-size">
                                                                                                {formatFileSize(att.fileSize)}
                                                                                            </span>
                                                                                        )}
                                                                                    </a>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        <div className="message-time">
                                                                            {message.timeStamp}
                                                                            {isOwnMessage && message.isReadByAll && (
                                                                                <span className="read-indicator"> ✓</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                <div ref={messagesEndRef} />
                                            </>
                                        )}
                                    </div>

                                    {/* Message Input */}
                                    <div className="message-input-area">
                                        {/* Attachment error */}
                                        {attachmentError && (
                                            <div className="attachment-error">
                                                <span>{attachmentError}</span>
                                                <button onClick={() => setAttachmentError('')}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        )}

                                        {/* Attachment previews */}
                                        {attachments.length > 0 && (
                                            <div className="attachment-previews">
                                                {attachments.map((att, index) => (
                                                    <div key={index} className="attachment-preview">
                                                        <FileText size={16} />
                                                        <span className="attachment-name">{att.fileName}</span>
                                                        <button
                                                            className="attachment-remove"
                                                            onClick={() => removeAttachment(index)}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Attachment info */}
                                        <div className="attachment-info">
                                            <span>PDF only • Max {MAX_FILE_SIZE_MB}MB • Up to {MAX_ATTACHMENTS} files</span>
                                            {attachments.length > 0 && !newMessage.trim() && (
                                                <span className="message-required-notice">• Message required with attachments</span>
                                            )}
                                        </div>

                                        <div className="message-input-container">
                                            {/* Hidden file input */}
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                                accept=".pdf,application/pdf"
                                                multiple
                                                style={{ display: 'none' }}
                                            />

                                            {/* Attachment button */}
                                            <button
                                                className="attachment-btn"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={attachments.length >= MAX_ATTACHMENTS || sendingMessage}
                                                title={attachments.length >= MAX_ATTACHMENTS ? `Maximum ${MAX_ATTACHMENTS} attachments` : 'Attach PDF'}
                                            >
                                                <Paperclip size={20} />
                                            </button>

                                            <textarea
                                                ref={messageInputRef}
                                                className="message-input"
                                                placeholder="Type a message..."
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                rows={1}
                                            />
                                            <button
                                                className="send-btn"
                                                onClick={handleSendMessage}
                                                disabled={!newMessage.trim() || sendingMessage}
                                            >
                                                <Send size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="no-thread-selected">
                                    <MessageCircle size={80} />
                                    <h3>Select a chat</h3>
                                    <p>Reach out and start a conversation,<br />your messages will show up here.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* New Conversation Modal */}
            {showNewConversation && event && (
                <NewICConversationModal
                    eventId={event.EventId}
                    onClose={() => setShowNewConversation(false)}
                    onStartConversation={handleStartConversation}
                />
            )}
        </>
    );
};

export default EventMessages;
